import { Trans } from '@lingui/react/macro'
import { t } from '@lingui/core/macro'

import type {
  BusinessSignupRequest,
  BusinessSignupResponse,
} from '@jotta/grpc-connect-openapi/signup'
import { useForm } from '@jotta/hooks'
import type { PartialMessage } from '@jotta/types/compat'
import { Divider } from '@jotta/ui/Divider'
import { Form } from '@jotta/ui/Form'
import { FormField } from '@jotta/ui/FormField'
import { TextInput } from '@jotta/ui/TextInput'
import { cn } from '@jotta/utils/css'

import { CTAButton } from './CTAButton'
import { PasswordField } from './PasswordField'
import {
  customerGroupCodeField,
  newsletterField,
  useAcceptedTermsField,
  useActivationCodeTerminology,
  useEmailField,
  useNameField,
  usePasswordField,
  useVoucherCodeField,
} from './signupForm'
import { TermsAndConditionsCheckbox } from './TermsAndConditionsCheckbox'

export function CreateBusinessUser({
  className,
  ctaClassName,
  isPending,
  signup,
  supportsVoucher,
  prefill,
  ctaLabelText,
  referralCode,
}: {
  className: string
  ctaClassName?: string
  isPending: boolean
  signup: (
    signup: PartialMessage<BusinessSignupRequest>,
  ) => Promise<BusinessSignupResponse>
  supportsVoucher: boolean
  prefill: { customerGroupCode: string; name?: string; email?: string }
  ctaLabelText?: string
  referralCode?: string
}) {
  const activationCodeTerminology = useActivationCodeTerminology()
  const { customerGroupCode } = prefill
  const form = useForm({
    fields: {
      companyName: {
        type: 'text',
        required: true,
      },
      name: useNameField(),
      email: useEmailField(customerGroupCode),
      password: usePasswordField(),
      customerGroupCode: customerGroupCodeField,
      voucherCode: useVoucherCodeField(
        customerGroupCode,
        voucherCustomerGroupCode =>
          form.set('customerGroupCode', voucherCustomerGroupCode),
      ),
      acceptedTerms: useAcceptedTermsField(),
      newsletter: newsletterField,
    },
    suppressIndicatorsForRequiredFields: true,
    defaultValues: () => prefill,
    submit: ({
      companyName,
      name,
      email,
      password,
      voucherCode,
      newsletter,
      customerGroupCode,
    }) =>
      signup({
        businessName: companyName,
        adminFullname: name,
        adminEmail: email,
        adminPassword: password,
        customerGroupCode,
        voucherCode,
        wantsNewsletter: Boolean(newsletter),
        referralCode: referralCode,
      }),
  })

  const {
    companyName,
    name,
    password,
    email,
    voucherCode,
    acceptedTerms,
    newsletter,
    isSubmitting,
  } = form

  return (
    <Form form={form} className={className}>
      <FormField label={t`Company Name`} field={companyName}>
        <TextInput {...companyName.inputProps} />
      </FormField>

      <Divider />

      <FormField label={t`Name of administrator`} field={name}>
        <TextInput {...name.inputProps} />
      </FormField>

      <FormField label={t`Email`} field={email}>
        <TextInput {...email.inputProps} />
      </FormField>

      <PasswordField field={password} />

      {supportsVoucher && (
        <FormField
          label={
            activationCodeTerminology ? t`Activation code` : t`Voucher code`
          }
          field={voucherCode}
        >
          <TextInput {...voucherCode.inputProps} />
        </FormField>
      )}

      <TermsAndConditionsCheckbox
        acceptedTerms={acceptedTerms}
        newsletter={newsletter}
      />

      <CTAButton
        className={cn('w-full', ctaClassName)}
        loading={isPending || isSubmitting}
        type="submit"
      >
        {ctaLabelText || <Trans>Create account</Trans>}
      </CTAButton>
    </Form>
  )
}
